import { AccountStatus, isValidAccountStatus, AccountLinks } from './AccountStatus';

export interface AccountLocale {
  readonly region: string;
  readonly timezone: string;
  readonly language: string;
}

export interface AccountOwner {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly phone: string | null;
}

/**
 * Account Domain Model
 * Represents an account organization in the system
 */
export interface Account {
  readonly id: string;
  readonly name: string;
  readonly status: AccountStatus;
  readonly timezone: string;
  readonly links: AccountLinks;
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
  readonly locale?: AccountLocale;
  readonly owner?: AccountOwner;
}

/**
 * Data required to create an Account
 */
export interface AccountData {
  id: string;
  name: string;
  status: string;
  timezone?: string;
  links: AccountLinks;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  locale?: AccountLocale;
  owner?: AccountOwner;
}

/**
 * Factory function to create an Account
 * Validates input and constructs an immutable Account object
 *
 * @throws Error if validation fails
 */
export function createAccount(data: AccountData): Account {
  // Validate required fields
  if (!data.id || data.id.trim() === '') {
    throw new Error('Account ID is required');
  }

  if (!data.name || data.name.trim() === '') {
    throw new Error('Account name is required');
  }

  if (!isValidAccountStatus(data.status)) {
    throw new Error(
      `Invalid account status: ${data.status}. Must be one of: verified, unverified, removed, cancelled`,
    );
  }

  // Parse dates if they're strings
  const createdAt = data.createdAt
    ? typeof data.createdAt === 'string'
      ? new Date(data.createdAt)
      : data.createdAt
    : undefined;

  const updatedAt = data.updatedAt
    ? typeof data.updatedAt === 'string'
      ? new Date(data.updatedAt)
      : data.updatedAt
    : undefined;

  return {
    id: data.id.trim(),
    name: data.name.trim(),
    status: data.status,
    timezone: data.timezone ?? '',
    links: data.links,
    createdAt,
    updatedAt,
    locale: data.locale,
    owner: data.owner,
  };
}
